// ---------------------------------------------------------------------------
// Tracking Pixel Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, jsString } from "../escape.js";

interface TrackingPlatform {
  type: "gtm" | "meta" | "google_analytics" | "tiktok" | "posthog" | "custom";
  trackingId: string;
  eventData?: Record<string, string>;
}

interface TrackingPixelProps {
  eventName?: string;
  platforms: TrackingPlatform[] | string[];
  eventData?: Record<string, string>;
}

function renderGtm(id: string): string {
  return `<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${jsString(id)}');</script>`;
}

function renderMetaPixel(id: string): string {
  return `<script>!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${jsString(id)}');fbq('track','PageView');</script>
<noscript><img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${attr(id)}&ev=PageView&noscript=1"></noscript>`;
}

function renderGoogleAnalytics(id: string): string {
  return `<script async src="https://www.googletagmanager.com/gtag/js?id=${attr(id)}"></script>
<script>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${jsString(id)}');</script>`;
}

function renderPostHog(id: string): string {
  return `<script>!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group identify setPersonProperties setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);posthog.init('${jsString(id)}',{api_host:'https://us.i.posthog.com',person_profiles:'identified_only'});</script>`;
}

function renderTikTok(id: string): string {
  return `<script>!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src=r+"?sdkid="+e+"&lib="+t;var s=document.getElementsByTagName("script")[0];s.parentNode.insertBefore(a,s)};ttq.load('${jsString(id)}');ttq.page();}(window,document,'ttq');</script>`;
}

function renderCustom(scriptUrl: string): string {
  return `<script src="${attr(scriptUrl)}"></script>`;
}

function wrapExternalIntegration(label: string, markup: string): string {
  return `<!-- Ease external tracking integration: ${label} -->\n${markup}`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as TrackingPixelProps;

  const platforms = Array.isArray(p.platforms) ? p.platforms : [];

  const scripts: string[] = [];

  for (const platform of platforms) {
    // Support both object form and simple string form
    const plat =
      typeof platform === "string"
        ? { type: platform as TrackingPlatform["type"], trackingId: "" }
        : (platform as TrackingPlatform);

    const id = plat.trackingId ?? "";
    if (!id) continue;

    switch (plat.type) {
      case "gtm":
        scripts.push(wrapExternalIntegration("Google Tag Manager", renderGtm(id)));
        break;
      case "meta":
        scripts.push(wrapExternalIntegration("Meta Pixel", renderMetaPixel(id)));
        break;
      case "google_analytics":
        scripts.push(wrapExternalIntegration("Google Analytics", renderGoogleAnalytics(id)));
        break;
      case "posthog":
        scripts.push(wrapExternalIntegration("PostHog", renderPostHog(id)));
        break;
      case "tiktok":
        scripts.push(wrapExternalIntegration("TikTok Pixel", renderTikTok(id)));
        break;
      case "custom":
        scripts.push(wrapExternalIntegration("Custom tracking script", renderCustom(id)));
        break;
    }
  }

  if (scripts.length === 0) {
    return "<!-- ease-external-tracking-integrations: none configured -->";
  }

  return `<!-- ease-external-tracking-integrations -->\n<div hidden data-ease-integration-surface="external-tracking" aria-hidden="true"></div>\n${scripts.join("\n")}`;
}

registerRenderer("tracking-pixel", { render });
